import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Truck } from 'lucide-react';
import { useTour } from '../contexts/TourContext';
import ProductTour from './Tour/ProductTour';
import { vehiclesTour } from '../tours/tourConfigs';
import VehicleCard from './Vehicles/VehicleCard';
import AddVehicleModal from './Vehicles/AddVehicleModal';
import API_URL from '../config';
import { readUserFromStorage, getSubscriptionTier } from '../utils/subscription';
import {
  getVehicleLimit,
  getVehicleLimitMessage,
  isFleetVehicleCeiling,
  getEnterpriseVehicleMailto,
} from '../config/pricingPublic';
import '../App.css';

function isAtVehicleLimit(tier, count) {
  const max = getVehicleLimit(tier);
  return count >= max;
}

const Vehicles = () => {
  const navigate = useNavigate();
  const { isTourComplete, markTourComplete } = useTour();
  const [showTour, setShowTour] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deletingVehicle, setDeletingVehicle] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('readiness');
  const [subscriptionTier, setSubscriptionTier] = useState('free');

  useEffect(() => {
    if (!isTourComplete('vehicles')) {
      setTimeout(() => setShowTour(true), 500);
    }
  }, [isTourComplete]);

  const handleTourFinish = () => {
    setShowTour(false);
    markTourComplete('vehicles');
  };

  const refreshTier = useCallback(async () => {
    const local = readUserFromStorage();
    let tier = getSubscriptionTier(local);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data) {
        const merged = { ...local, ...data };
        tier = getSubscriptionTier(merged);
      }
    } catch {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/api/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data?.tier) {
          tier = String(data.tier).toLowerCase().trim();
        }
      } catch {
        /* keep local tier */
      }
    }
    setSubscriptionTier(tier);
  }, []);

  useEffect(() => {
    refreshTier();
  }, [refreshTier]);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/vehicles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVehicles(response.data);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError('Failed to load vehicles. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowAddModal(true);
  };

  const handleDelete = (vehicle) => {
    setDeletingVehicle(vehicle);
  };

  const handleConfirmDelete = async () => {
    if (!deletingVehicle) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/vehicles/${deletingVehicle.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDeletingVehicle(null);
      await fetchVehicles();
      await refreshTier();
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      alert('Failed to delete vehicle. Please try again.');
    }
  };

  const handleLogDVIR = (vehicle) => {
    navigate(`/vehicles/${vehicle.id}`, { state: { showDVIR: true } });
  };

  const handleModalSuccess = () => {
    fetchVehicles();
    refreshTier();
    setEditingVehicle(null);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingVehicle(null);
  };

  const filteredAndSortedVehicles = () => {
    let filtered = vehicles;

    // Filter
    if (filter === 'active') {
      filtered = filtered.filter((v) => v.isActive);
    } else if (filter === 'inactive') {
      filtered = filtered.filter((v) => !v.isActive);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'readiness') {
        return (b.readinessScore || 0) - (a.readinessScore || 0);
      } else if (sortBy === 'inspection') {
        if (!a.lastInspectionDate && !b.lastInspectionDate) return 0;
        if (!a.lastInspectionDate) return 1;
        if (!b.lastInspectionDate) return -1;
        return new Date(a.lastInspectionDate) - new Date(b.lastInspectionDate);
      } else if (sortBy === 'name') {
        const nameA = a.nickname || `${a.year} ${a.make} ${a.model}`;
        const nameB = b.nickname || `${b.year} ${b.make} ${b.model}`;
        return nameA.localeCompare(nameB);
      }
      return 0;
    });

    return filtered;
  };

  const getSummaryStats = () => {
    const total = vehicles.length;
    const inspectionsDueSoon = vehicles.filter((v) => {
      if (!v.lastInspectionDate) return false;
      const lastInspection = new Date(v.lastInspectionDate);
      const daysSince = Math.floor(
        (new Date().getTime() - lastInspection.getTime()) / (1000 * 60 * 60 * 24)
      );
      return daysSince >= 350 && daysSince <= 365;
    }).length;
    const openDefects = vehicles.filter((v) => v.hasOpenDefects).length;

    return { total, inspectionsDueSoon, openDefects };
  };

  const stats = getSummaryStats();
  const atLimit = isAtVehicleLimit(subscriptionTier, vehicles.length);
  const limitHint = getVehicleLimitMessage(subscriptionTier);
  const vehicleLimit = getVehicleLimit(subscriptionTier);
  const fleetCeiling = atLimit && isFleetVehicleCeiling(subscriptionTier);

  const addVehicleButton = (
    <div className="drivers-add-driver-wrap">
      <button
        type="button"
        className={atLimit ? 'btn btn-secondary drivers-add-btn--disabled' : 'btn btn-primary'}
        disabled={atLimit}
        title={atLimit ? limitHint : undefined}
        onClick={() => {
          if (!atLimit) setShowAddModal(true);
        }}
      >
        + Add Vehicle
      </button>
      {atLimit && <p className="drivers-limit-hint">{limitHint}</p>}
    </div>
  );

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading vehicles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <ProductTour
        steps={vehiclesTour}
        run={showTour}
        onFinish={handleTourFinish}
        tourName="vehicles"
      />
      
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1 className="page-title">My Vehicles</h1>
            <p className="page-subtitle">Manage your fleet and compliance</p>
            {vehicleLimit > 0 && (
              <p className="drivers-usage-subtitle">
                Vehicles: {vehicles.length} of {vehicleLimit} used
              </p>
            )}
          </div>
          {addVehicleButton}
        </div>
      </div>

      {atLimit && (
        <div className="drivers-plan-banner">
          <p>{limitHint}</p>
          {fleetCeiling ? (
            <a className="btn btn-secondary drivers-plan-banner-cta" href={getEnterpriseVehicleMailto()}>
              Contact us about Enterprise
            </a>
          ) : (
            <button
              type="button"
              className="btn btn-secondary drivers-plan-banner-cta"
              onClick={() => navigate('/pricing')}
            >
              View plans
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="dashboard-error">
          <p>{error}</p>
        </div>
      )}

      {/* Summary Stats */}
      <div className="vehicles-summary">
        <div className="summary-card">
          <div className="summary-value">{stats.total}</div>
          <div className="summary-label">Total Vehicles</div>
        </div>
        <div className="summary-card">
          <div className="summary-value warning">{stats.inspectionsDueSoon}</div>
          <div className="summary-label">Inspections Due Soon</div>
        </div>
        <div className="summary-card">
          <div className="summary-value error">{stats.openDefects}</div>
          <div className="summary-label">Open Defects</div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="vehicles-controls">
        <div className="filter-group">
          <label className="filter-label">Filter:</label>
          <select
            className="filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="sort-group">
          <label className="filter-label">Sort by:</label>
          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="readiness">Readiness Score</option>
            <option value="inspection">Next Inspection</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      {/* Vehicles Grid */}
      {filteredAndSortedVehicles().length === 0 ? (
        <div className="empty-state-enhanced">
          <div className="empty-icon"><Truck size={48} /></div>
          <h3>No vehicles yet</h3>
          <p>
            {filter !== 'all'
              ? 'No vehicles match your filter.'
              : vehicleLimit === 0
                ? 'Upgrade your plan to add vehicles and track inspections and maintenance.'
                : 'Add your first vehicle to start tracking compliance, inspections, and maintenance.'}
          </p>
          {filter === 'all' && !atLimit && vehicleLimit > 0 && (
            <div className="empty-cta">
              <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                Add Vehicle
              </button>
            </div>
          )}
          {filter === 'all' && atLimit && (
            <div className="empty-cta">
              {fleetCeiling ? (
                <a className="btn btn-secondary" href={getEnterpriseVehicleMailto()}>
                  Contact us about Enterprise
                </a>
              ) : (
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/pricing')}>
                  Upgrade plan
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="vehicles-grid">
          {filteredAndSortedVehicles().map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onLogDVIR={handleLogDVIR}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AddVehicleModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        vehicle={editingVehicle}
      />

      {/* Delete Confirmation Modal */}
      {deletingVehicle && (
        <div className="modal-overlay" onClick={() => setDeletingVehicle(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="delete-modal-title">Delete Vehicle</h3>
            <p className="delete-modal-text">
              Are you sure you want to delete{' '}
              <strong>
                {deletingVehicle.nickname ||
                  `${deletingVehicle.year} ${deletingVehicle.make} ${deletingVehicle.model}`}
              </strong>
              ? This action cannot be undone.
            </p>
            <div className="delete-modal-actions">
              <button
                className="modal-button cancel"
                onClick={() => setDeletingVehicle(null)}
              >
                Cancel
              </button>
              <button className="modal-button delete" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
